import React from 'react'

// Uses the explicit label/htmlFor pattern (rather than the input-
// inside-label form most other v7 checkboxes/radios use) so the
// `afterLabel` slot can flow inline at the end of the wrapped
// label text. With the implicit form, afterLabel inside the label
// would make its click toggle the checkbox; afterLabel outside the
// label would break the inline-with-wrap flow we want — the popover
// icon needs to sit at the very end of the visible label text, even
// when that text wraps to a second line.
//
// The `before:content-['']` trick on the label extends its
// click-target by 0.5rem into the inline-start direction, swallowing
// the visible gap between the checkbox and the label so the donor
// can click anywhere from the checkbox's edge through the label
// text without hitting a dead zone. Direct `-ms-2 ps-2` on the label
// achieves a similar effect but has subtle browser differences in
// how wrapped lines line up; the pseudo is more predictable.
const CheckBox = ({ id, label, error, hint, afterLabel, className, ...inputProps }) => {
  const labelId = `${id}-label`
  const errorId = error && `${id}-error`
  const hintId = hint && `${id}-hint`
  // Both ids feed aria-describedby when present so the screen reader
  // announces the input's name → state → error → hint in that order
  // (the order tokens appear in aria-describedby).
  const describedBy = [ errorId, hintId ].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex items-start gap-2">
      <input
        type="checkbox"
        id={id}
        // Merge so a caller's className can't drop form-checkbox's base styles.
        className={`form-checkbox mt-0.5 ${className || ''}`.trim()}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : undefined}
        {...inputProps}
      />
      {/* flex-1 keeps the column from shrinking around an unusually
          short label; min-w-0 lets long hint / error copy wrap inside
          the column instead of expanding it past the parent's width. */}
      <div className="flex-1 min-w-0">
        <div className="space-x-2">
          {/* Cursor (pointer, or not-allowed when disabled) comes from the
              .form-checkbox-label CSS rule, not a utility here — a utility
              would beat the disabled rule across @layers. */}
          <label
            className="form-checkbox-label before:content-[''] before:-ms-2 before:ps-2"
            id={labelId}
            htmlFor={id}>
            {label}
          </label>
          {afterLabel}
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-sm text-danger">
            {error}
          </p>
        )}
        {hint && (
          <p id={hintId} className="mt-1 text-sm text-gray-600">
            {hint}
          </p>
        )}
      </div>
    </div>
  )
}

export default CheckBox
